import { createElement, useMemo } from 'react';

import { cn } from '@crehana/compass.ui/utils';
import { useFeatureFlag } from '@crehana/feature-flags/react';
import { notEmpty } from '@crehana/utils';

import { PayrollReceiptWidget } from '../../widgets/payroll-receipt-widget';
import { ErrorLoadingWidgets } from './components/error-loading-widgets';
import { GridWidgetsEmptyState } from './components/grid-widgets-empty-state';
import { useWidgetsLayout } from './hooks/use-widgets-layout';
import type { TWidgetId } from './hooks/use-widgets-layout/types';
import type { TDynamicGridWidgetsProps, TWidget } from './types';

/**
 * @description This component is used to render a dynamic grid of widgets based
 *              on the widgets config layout from the backend.
 *
 * @example
 * const widgets = [
 *  {
 *    id: 'widget-1',
 *    component: Widget1,
 *    className: 'tw-max-h-fit', // Optional, will be merged with the widget config className if provided
 *    props: {
 *      title: 'Widget 1',
 *      customPropHere: 'some value',
 *    },
 *  },
 * ]
 *
 * <DynamicGridWidgets widgets={widgets} />
 */
export const DynamicGridWidgets: React.FC<
  React.PropsWithChildren<TDynamicGridWidgetsProps>
> = ({ widgets, children }) => {
  const { widgetsConfigLayout, error, refetch } = useWidgetsLayout();

  const { flagValue: isPayrollReceiptsWidgetEnabled } = useFeatureFlag(
    'PAYROLL_RECEIPTS_WIDGET',
  );

  const widgetsToRender = useMemo(() => {
    const widgetsMap = new Map<TWidgetId, (typeof widgets)[number]>(
      widgets.map(widget => [widget.id, widget]),
    );

    const filteredWidgets = widgetsConfigLayout
      .map(widgetConfig => {
        const widget = widgetsMap.get(widgetConfig.id);

        if (!widget) {
          // NOTE: This should not happen, but it's an emergency fallback.
          return undefined;
        }

        return {
          ...widget,
          className: cn(widgetConfig.className, widget.className),
        };
      })
      .filter(notEmpty);

    if (isPayrollReceiptsWidgetEnabled) {
      const talentNewsIndex = filteredWidgets.findIndex(
        widget => widget.id === 'talent-news',
      );
      const insertIndex =
        talentNewsIndex >= 0 ? talentNewsIndex + 1 : filteredWidgets.length;
      filteredWidgets.splice(insertIndex, 0, {
        // @ts-expect-error - This is a temporary mvp card, once the real product is implemented this should be removed
        id: 'payroll-receipts-widget',
        className:
          'tw-col-span-12 sm:tw-col-span-12 md:tw-col-span-6 tw-max-h-[39.2rem]',
        component: PayrollReceiptWidget,
        props: {},
      } satisfies TWidget<typeof PayrollReceiptWidget>);
    }

    return filteredWidgets;
  }, [
    widgetsConfigLayout,
    widgets,
    // isTimeAndAttendanceFeatureFlagEnabled,
    isPayrollReceiptsWidgetEnabled,
  ]);

  const renderWidgets = () => {
    if (error) {
      return <ErrorLoadingWidgets onRetry={refetch} />;
    }

    if (widgetsToRender.length === 0) {
      return <GridWidgetsEmptyState />;
    }

    return widgetsToRender.map(widget => {
      return (
        <div
          key={widget.id}
          className={cn(
            'tw-col-span-12 sm:tw-col-span-6 tw-max-h-[35.2rem]',
            widget.className,
          )}
        >
          {createElement(widget.component, widget.props ?? {})}
        </div>
      );
    });
  };

  return (
    <div
      data-testid="home-widgets-dynamic-grid"
      className="tw-grid tw-grid-cols-12 tw-gap-1.6 tw-pb-1.6"
    >
      {renderWidgets()}
      {children}
    </div>
  );
};
